import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Trophy,
  Banknote,
  Zap,
  Loader2,
  Users,
  Crown,
  Clock,
  Calendar,
  TrendingUp,
  Award,
  Spade,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/features/auth/auth-store";
import { useTableLobby, type TableSummary } from "@/features/poker/hooks/useTableLobby";
import { apiQuickJoin, apiJoin } from "@/features/poker/api";
import { usePokerStore } from "@/features/poker/poker-store";
import { CashLobby } from "@/features/lobby/components/CashLobby";
import { TournamentLobby } from "@/features/lobby/components/TournamentLobby";
import { BuyInDialog } from "@/features/lobby/components/BuyInDialog";
import { useToast } from "@/components/ui/use-toast";

const Play = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "cash";

  const authToken = useAuthStore((s) => s.token);
  const userChips = useAuthStore((s) => s.user?.chips ?? 0);
  const setSession = usePokerStore((state) => state.setSession);

  const { data: tables = [], isLoading: tablesLoading } = useTableLobby();
  const [joining, setJoining] = useState(false);
  const [buyInTarget, setBuyInTarget] = useState<TableSummary | null>(null);

  const requireAuth = () => {
    if (!authToken) {
      toast({ title: "Login required", description: "Please log in to play", variant: "destructive" });
      navigate("/login");
      return false;
    }
    return true;
  };

  const doJoin = async (targetRoom: string, buyIn?: number) => {
    setJoining(true);
    try {
      const data = await apiJoin({ room: targetRoom, name: "", authToken: authToken!, buyIn });
      setSession({ room: data.room, playerId: data.playerId, token: data.token });
      navigate("/table");
    } catch (err) {
      toast({
        title: "Could not join",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setJoining(false);
    }
  };

  const handleQuickJoin = async (gameMode: "tournament" | "cash") => {
    if (!requireAuth()) return;
    setJoining(true);
    try {
      const data = await apiQuickJoin({ authToken: authToken!, gameMode });
      setSession({ room: data.room, playerId: data.playerId, token: data.token });
      navigate("/table");
    } catch (err) {
      toast({
        title: "Could not join",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setJoining(false);
    }
  };

  const handleCashTableJoin = (table: TableSummary) => {
    if (!requireAuth()) return;
    setBuyInTarget(table);
  };

  const handleBuyInConfirm = (buyIn: number) => {
    if (!buyInTarget) return;
    setBuyInTarget(null);
    doJoin(buyInTarget.room, buyIn);
  };

  const handleJoinRoom = async (targetRoom: string) => {
    if (!requireAuth()) return;
    doJoin(targetRoom);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-4 max-w-screen-xl mx-auto">
        <Tabs defaultValue={defaultTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="cash" className="flex items-center gap-1.5">
              <Banknote className="h-4 w-4" />
              {t("play_tab_cash")}
            </TabsTrigger>
            <TabsTrigger value="sng" className="flex items-center gap-1.5">
              <Spade className="h-4 w-4" />
              {t("play_tab_sng")}
            </TabsTrigger>
            <TabsTrigger value="tournaments" className="flex items-center gap-1.5">
              <Trophy className="h-4 w-4" />
              {t("play_tab_tournaments")}
            </TabsTrigger>
          </TabsList>

          {/* ── Cash Game ── */}
          <TabsContent value="cash" className="mt-4 space-y-4">
            <Button
              onClick={() => handleQuickJoin("cash")}
              disabled={joining || !authToken}
              className="w-full bg-green-700 hover:bg-green-600 text-white"
              size="lg"
            >
              {joining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
              {t("play_quick_cash")}
            </Button>
            {tablesLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <CashLobby tables={tables} onJoin={handleCashTableJoin} joining={joining} />
            )}
          </TabsContent>

          {/* ── Sit & Go ── */}
          <TabsContent value="sng" className="mt-4 space-y-4">
            <Button
              onClick={() => handleQuickJoin("tournament")}
              disabled={joining || !authToken}
              className="w-full bg-yellow-700 hover:bg-yellow-600 text-white"
              size="lg"
            >
              {joining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
              {t("play_quick_sng")}
            </Button>
            {tablesLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <TournamentLobby tables={tables} onJoin={handleJoinRoom} joining={joining} />
            )}
          </TabsContent>

          {/* ── Tournaments ── */}
          <TabsContent value="tournaments" className="mt-4 space-y-6">
            {/* Featured Tournament */}
            <Card className="glass p-5 border-destructive/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-destructive/10 to-transparent pointer-events-none" />
              <div className="relative">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
                        <span className="text-xs font-semibold text-destructive">LIVE NOW</span>
                      </div>
                      <Badge className="bg-info text-info-foreground">Featured</Badge>
                    </div>
                    <h3 className="text-xl font-bold mb-1">Friday Night Championship</h3>
                    <p className="text-sm text-muted-foreground">Beginner-friendly freeroll tournament</p>
                  </div>
                  <Trophy className="w-8 h-8 text-secondary shrink-0" />
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="glass p-2 text-center">
                    <Users className="w-4 h-4 mx-auto mb-1 text-accent" />
                    <p className="text-xs text-muted-foreground mb-0.5">Players</p>
                    <p className="font-bold text-sm">45/100</p>
                  </div>
                  <div className="glass p-2 text-center">
                    <Crown className="w-4 h-4 mx-auto mb-1 text-secondary" />
                    <p className="text-xs text-muted-foreground mb-0.5">Prize Pool</p>
                    <p className="font-bold text-sm text-secondary">50K</p>
                  </div>
                  <div className="glass p-2 text-center">
                    <Clock className="w-4 h-4 mx-auto mb-1 text-destructive" />
                    <p className="text-xs text-muted-foreground mb-0.5">Starts In</p>
                    <p className="font-bold text-sm">2:45</p>
                  </div>
                </div>

                <Button className="w-full bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold">
                  Join Tournament
                </Button>
              </div>
            </Card>

            {/* Upcoming */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold text-muted-foreground">Upcoming Today</h3>
              </div>
              <div className="space-y-2">
                {[
                  { time: "18:00", name: "Weekly Challenge", players: "0/50", prize: "25K", level: "Intermediate", buyin: "Free" },
                  { time: "20:00", name: "Friends Tournament", players: "0/20", prize: "10K", level: "All Levels", buyin: "500" },
                  { time: "22:00", name: "Late Night Grind", players: "0/30", prize: "15K", level: "Advanced", buyin: "1000" },
                ].map((tournament, i) => (
                  <Card key={i} className="glass p-4 hover-lift">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs border-info/50 text-info-foreground">{tournament.time}</Badge>
                          <Badge className="text-xs bg-info text-info-foreground">{tournament.level}</Badge>
                        </div>
                        <h3 className="font-bold mb-1">{tournament.name}</h3>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" /> {tournament.players}
                          </span>
                          <span className="flex items-center gap-1">
                            <Crown className="w-3 h-3 text-secondary" /> {tournament.prize}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-secondary">
                          {tournament.buyin === "Free" ? "FREE" : tournament.buyin}
                        </p>
                        <p className="text-xs text-muted-foreground">Buy-in</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="w-full">Register</Button>
                  </Card>
                ))}
              </div>
            </section>

            {/* Leaderboards */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-accent" />
                <h3 className="text-sm font-semibold text-muted-foreground">Leaderboards</h3>
              </div>
              <div className="space-y-2">
                {[
                  { rank: 1, name: "PokerPro_Mike", points: "12,450", badge: "🥇" },
                  { rank: 2, name: "AceQueen_Sarah", points: "11,890", badge: "🥈" },
                  { rank: 3, name: "FullHouse_Tom", points: "10,230", badge: "🥉" },
                ].map((player) => (
                  <Card key={player.rank} className="glass p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl w-8 text-center">{player.badge}</span>
                        <div>
                          <p className="font-semibold text-sm">{player.name}</p>
                          <p className="text-xs text-muted-foreground">Weekly points</p>
                        </div>
                      </div>
                      <p className="text-sm font-bold">{player.points}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </section>

            {/* Achievements */}
            <Card className="glass p-4 border-secondary/20">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-4 h-4 text-secondary" />
                <h3 className="font-bold text-sm">Your Achievements</h3>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {["🏆", "🎯", "⚡", "🔥", "💎"].map((emoji, i) => (
                  <div
                    key={i}
                    className="flex-shrink-0 w-14 h-14 glass flex items-center justify-center text-xl"
                  >
                    {emoji}
                  </div>
                ))}
                <div className="flex-shrink-0 w-14 h-14 glass flex items-center justify-center text-sm text-muted-foreground border-dashed">
                  +3
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {buyInTarget && (
        <BuyInDialog
          open
          onClose={() => setBuyInTarget(null)}
          onConfirm={handleBuyInConfirm}
          minBuyIn={buyInTarget.minBuyIn}
          maxBuyIn={buyInTarget.maxBuyIn}
          bb={buyInTarget.bb}
          joining={joining}
          userChips={userChips}
        />
      )}
    </div>
  );
};

export default Play;
